import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from './ui/badge';
import { CalendarBlank, User, DotsSixVertical } from '@phosphor-icons/react';

function SortableTaskCard({ task, prioriteOptions, isOverdue, isToday, onClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const priorite = prioriteOptions?.find(p => p.value === task.priorite);
  const overdue = isOverdue?.(task.date_echeance) && task.statut !== 'terminee';
  const today = isToday?.(task.date_echeance);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-[#E8E6E1] rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onClick?.(task)}
      data-testid={`kanban-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-[#8DB0A1] hover:text-[#2D5A4C]"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          data-testid={`kanban-drag-${task.id}`}
        >
          <DotsSixVertical size={14} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-[#1A2421] line-clamp-2 ${task.statut === 'terminee' ? 'line-through opacity-50' : ''}`}>
            {task.titre}
          </p>
          {task.description && (
            <p className="text-[11px] text-[#5E6D66] mt-0.5 line-clamp-1">{task.description}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {priorite && (
              <span className={`text-[10px] font-semibold ${priorite.color}`}>
                {priorite.label}
              </span>
            )}
            {task.date_echeance && (
              <span className={`text-[10px] flex items-center gap-0.5 ${overdue ? 'text-red-500 font-medium' : today ? 'text-orange-500' : 'text-[#5E6D66]'}`}>
                <CalendarBlank size={10} />
                {new Date(task.date_echeance).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
              </span>
            )}
            {task.type_tache && (
              <Badge variant="outline" className="text-[9px] py-0 h-4">
                {task.type_tache}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskCardOverlay({ task, prioriteOptions }) {
  const priorite = prioriteOptions?.find(p => p.value === task.priorite);
  return (
    <div className="bg-white border-2 border-[#2D5A4C] rounded-lg p-3 shadow-xl w-[260px] rotate-2">
      <p className="text-sm font-medium text-[#1A2421] line-clamp-2">{task.titre}</p>
      {priorite && (
        <span className={`text-[10px] font-semibold ${priorite.color}`}>{priorite.label}</span>
      )}
    </div>
  );
}

const KANBAN_COLUMNS = [
  { id: 'a_faire', label: 'A faire', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
  { id: 'en_cours', label: 'En cours', color: 'bg-yellow-500', bgColor: 'bg-yellow-50' },
  { id: 'en_attente', label: 'En attente', color: 'bg-orange-500', bgColor: 'bg-orange-50' },
  { id: 'terminee', label: 'Terminee', color: 'bg-green-500', bgColor: 'bg-green-50' },
  { id: 'annulee', label: 'Annulee', color: 'bg-gray-500', bgColor: 'bg-gray-50' },
];

export function KanbanBoard({ tasks, onUpdateTask, onEditTask, prioriteOptions, isOverdue, isToday }) {
  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const getTasksByStatus = (status) => tasks.filter(t => t.statut === status);

  const handleDragStart = (event) => {
    const task = tasks.find(t => t.id === event.active.id);
    setActiveTask(task || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);
    if (!over) return;

    const taskId = active.id;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Determine target column
    let targetStatus = null;
    // Check if dropped on a column container
    if (KANBAN_COLUMNS.some(c => c.id === over.id)) {
      targetStatus = over.id;
    } else {
      // Dropped on another task — find that task's status
      const overTask = tasks.find(t => t.id === over.id);
      if (overTask) targetStatus = overTask.statut;
    }

    if (targetStatus && targetStatus !== task.statut) {
      onUpdateTask?.(taskId, { statut: targetStatus });
    }
  };

  const handleDragOver = (event) => {
    // Allow drag over columns
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {KANBAN_COLUMNS.map((col) => {
          const columnTasks = getTasksByStatus(col.id);
          return (
            <div
              key={col.id}
              className={`flex-shrink-0 w-[280px] rounded-xl ${col.bgColor} border border-[#E8E6E1]/60`}
              data-testid={`kanban-column-${col.id}`}
            >
              <div className="p-3 flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                <h3 className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider">{col.label}</h3>
                <span className="text-[10px] text-[#5E6D66] bg-white/70 px-1.5 py-0.5 rounded-full font-medium ml-auto">
                  {columnTasks.length}
                </span>
              </div>
              <SortableContext
                items={columnTasks.map(t => t.id)}
                strategy={verticalListSortingStrategy}
                id={col.id}
              >
                <div className="px-2.5 pb-2.5 space-y-2 min-h-[80px]" id={col.id}>
                  {columnTasks.length === 0 && (
                    <div className="text-center py-6 text-[11px] text-[#8DB0A1]">
                      Deposer ici
                    </div>
                  )}
                  {columnTasks.map((task) => (
                    <SortableTaskCard
                      key={task.id}
                      task={task}
                      prioriteOptions={prioriteOptions}
                      isOverdue={isOverdue}
                      isToday={isToday}
                      onClick={onEditTask}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          );
        })}
      </div>

      <DragOverlay>
        {activeTask ? (
          <TaskCardOverlay task={activeTask} prioriteOptions={prioriteOptions} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
